import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  EmptyState,
  NumberField,
  Pill,
  SectionHeader,
  SelectField,
  TextField,
} from "../components/design-system/index.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import {
  deleteByokKey,
  getProviders,
  getUsage,
  patchAiSettings,
  setByokKey,
  type AiBillingMode,
  type AiProvider,
  type AiProvidersResponse,
} from "../lib/api-modules/ai.js";

import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";
/**
 * /settings/ai — AI provider, billing mode, BYOK key management,
 * monthly cap, and per-provider default model selection. Composed
 * from design-system primitives only.
 *
 * BYOK security:
 *   - The user's API key is sent ONCE to /v1/settings/ai/byok and
 *     immediately envelope-encrypted with the tenant DEK on the server
 *     side. The key is never returned to the client after that.
 *   - The "Test key" button validates the key by making a tiny test
 *     call to the provider before storing.
 *   - "Disconnect" clears the encrypted blob; if billing_mode was
 *     "byok" + that was the only configured key, billing_mode flips
 *     back to "hearth" automatically.
 *
 * Bedrock:
 *   - Hearth-paid only in v1 (cross-account IAM BYOK is Phase 17).
 *   - The /settings/ai page disables the BYOK toggle when Bedrock is
 *     selected.
 */
export function SettingsAiPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const providersQuery = useQuery({
    queryKey: ["ai", "providers"],
    queryFn: getProviders,
  });
  const usageQuery = useQuery({
    queryKey: ["ai", "usage"],
    queryFn: getUsage,
  });

  const patchMutation = useMutation({
    mutationFn: patchAiSettings,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["ai", "providers"] });
      toast.show("Settings updated.");
    },
    onError: (err) => toast.show(`Update failed: ${err.message}`),
  });

  const setKeyMutation = useMutation({
    mutationFn: setByokKey,
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: ["ai", "providers"] });
      toast.show(
        `Key validated (${data.latency_ms}ms via ${data.model_used_for_test ?? "test"}).`,
      );
    },
    onError: (err) => toast.show(`Key validation failed: ${err.message}`),
  });

  const deleteKeyMutation = useMutation({
    mutationFn: deleteByokKey,
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: ["ai", "providers"] });
      toast.show(`BYOK key removed. Billing mode: ${data.billing_mode_after}.`);
    },
    onError: (err) => toast.show(`Remove failed: ${err.message}`),
  });

  if (providersQuery.isLoading) {
    return <div className="p-6 text-sm text-[var(--color-faint)]">Loading…</div>;
  }
  if (providersQuery.error) {
    return (
      <EmptyState
        title="Failed to load AI settings"
        description={providersQuery.error.message}
      />
    );
  }
  const data = providersQuery.data!;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <SectionHeader
        title="AI settings"
        sub="Pick a provider, manage BYOK keys, and set your monthly token cap."
      />

      <Toast message={toast.message} />

      {/* === Provider toggle === */}
      <Card padded>
        <div className="space-y-4">
          <div className="font-medium">Provider</div>
          <div className="text-sm text-[var(--color-faint)]">
            Hearth supports three first-class LLM providers. Switching the active provider applies to NEW conversations; existing threads continue with their original provider.
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {data.providers.map((p) => (
              <ProviderTile
                key={p.name}
                provider={p}
                active={data.active_provider === p.name}
                onSelect={() =>
                  patchMutation.mutate({ ai_provider: p.name })
                }
              />
            ))}
          </div>
        </div>
      </Card>

      {/* === Billing mode === */}
      <Card padded>
        <div className="space-y-4">
          <div className="font-medium">Billing mode</div>
          <div className="text-sm text-[var(--color-faint)]">
            <strong>Hearth-paid</strong> uses Hearth's API key and counts against your monthly token cap. <strong>BYOK</strong> uses your own API key — no Hearth-side cap; your provider account does the billing.
            {data.active_provider === "bedrock" && (
              <div className="mt-2 italic">
                Bedrock is Hearth-paid only in v1 (cross-account IAM BYOK is Phase 17).
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {(["hearth", "byok"] as AiBillingMode[]).map((mode) => (
              <Button
                key={mode}
                variant={
                  data.active_billing_mode === mode ? "primary" : "secondary"
                }
                size="sm"
                disabled={
                  patchMutation.isPending ||
                  (mode === "byok" && data.active_provider === "bedrock")
                }
                onClick={() =>
                  patchMutation.mutate({ ai_billing_mode: mode })
                }
              >
                {mode === "hearth" ? "Hearth-paid" : "BYOK"}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* === BYOK keys === */}
      <Card padded>
        <div className="space-y-4">
          <div className="font-medium">BYOK API keys</div>
          <BYOKBlock
            label="Anthropic API key"
            provider="claude"
            configured={
              data.providers.find((p) => p.name === "claude")?.byok_configured ??
              false
            }
            onSet={(api_key) => setKeyMutation.mutate({ provider: "claude", api_key })}
            onDelete={() => deleteKeyMutation.mutate("claude")}
            pending={setKeyMutation.isPending || deleteKeyMutation.isPending}
          />
          <BYOKBlock
            label="OpenAI API key"
            provider="openai"
            configured={
              data.providers.find((p) => p.name === "openai")?.byok_configured ??
              false
            }
            onSet={(api_key) => setKeyMutation.mutate({ provider: "openai", api_key })}
            onDelete={() => deleteKeyMutation.mutate("openai")}
            pending={setKeyMutation.isPending || deleteKeyMutation.isPending}
          />
        </div>
      </Card>

      {/* === Default models === */}
      <Card padded>
        <div className="space-y-4">
          <div className="font-medium">Default model per provider</div>
          {data.providers.map((p) => (
            <SelectField
              key={p.name}
              label={p.display_name}
              value={p.default_model}
              onChange={(e) => {
                const update: Parameters<typeof patchAiSettings>[0] = {};
                if (p.name === "claude") update.default_claude_model = e.target.value;
                if (p.name === "openai") update.default_openai_model = e.target.value;
                if (p.name === "bedrock")
                  update.default_bedrock_model = e.target.value;
                patchMutation.mutate(update);
              }}
            >
              {p.available_models.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </SelectField>
          ))}
        </div>
      </Card>

      {/* === Monthly cap === */}
      <Card padded>
        <MonthlyCapBlock
          providers={data}
          usage={usageQuery.data}
          onUpdate={(monthly_token_cap) =>
            patchMutation.mutate({ monthly_token_cap })
          }
        />
      </Card>
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.settingsAi} />
      </div>
    </div>
  );
}

function ProviderTile({
  provider,
  active,
  onSelect,
}: {
  provider: AiProvidersResponse["providers"][number];
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        "rounded-md border p-3 text-left transition-colors",
        active
          ? "border-[var(--color-accent)] bg-[var(--color-accent-soft)]"
          : "border-[var(--color-border-default)] hover:bg-[var(--color-elevated)]",
      ].join(" ")}
    >
      <div className="font-medium">{provider.display_name}</div>
      <div className="text-xs text-[var(--color-faint)] mt-1">{provider.default_model}</div>
      {provider.byok_configured && (
        <Pill tone="success" className="mt-2 text-[10px]">
          BYOK configured
        </Pill>
      )}
    </button>
  );
}

function BYOKBlock({
  label,
  provider,
  configured,
  onSet,
  onDelete,
  pending,
}: {
  label: string;
  provider: AiProvider;
  configured: boolean;
  onSet: (apiKey: string) => void;
  onDelete: () => void;
  pending: boolean;
}) {
  const [apiKey, setApiKey] = useState("");
  if (configured) {
    return (
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium">{label}</div>
          <div className="text-xs text-[var(--color-faint)]">
            Configured. Hearth never returns the key to the client.
          </div>
        </div>
        <ConfirmButton
          label="Disconnect"
          confirmLabel="Confirm disconnect"
          isPending={pending}
          onConfirm={onDelete}
        />
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <TextField
        label={label}
        type="password"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        placeholder={
          provider === "claude" ? "sk-ant-…" : provider === "openai" ? "sk-…" : ""
        }
        helper="Sent to Hearth once, validated, then envelope-encrypted with your tenant data key."
      />
      <Button
        variant="secondary"
        size="sm"
        disabled={!apiKey || pending}
        onClick={() => {
          onSet(apiKey);
          setApiKey("");
        }}
      >
        Test &amp; save
      </Button>
    </div>
  );
}

function MonthlyCapBlock({
  providers,
  usage,
  onUpdate,
}: {
  providers: AiProvidersResponse;
  usage: ReturnType<typeof getUsage> extends Promise<infer T> ? T | undefined : undefined;
  onUpdate: (cap: number) => void;
}) {
  const [draft, setDraft] = useState(providers.monthly_token_cap);
  return (
    <div className="space-y-4">
      <div className="font-medium">Monthly token cap (Hearth-paid only)</div>
      <div className="text-sm text-[var(--color-faint)]">
        Capped at the request-start of every Hearth-paid chat. BYOK is uncapped (your provider account enforces).
      </div>
      <div className="flex items-end gap-2">
        <NumberField
          label="Cap (tokens)"
          value={draft}
          onChange={(e) => setDraft(parseInt(e.target.value, 10))}
          step={100_000}
          min={100_000}
          max={50_000_000}
        />
        <Button
          size="sm"
          variant="primary"
          onClick={() => onUpdate(draft)}
          disabled={draft === providers.monthly_token_cap}
        >
          Save
        </Button>
      </div>
      {usage && (
        <div className="text-xs text-[var(--color-faint)]">
          This month: <span className="font-mono">{usage.hearth_tokens_used.toLocaleString()}</span> / {usage.cap.toLocaleString()} ({usage.cap_pct}%) — total cost approx ${(usage.total_cost_cents / 100).toFixed(2)}
        </div>
      )}
    </div>
  );
}
